import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Modal,
  Box,
  Button,
  SpaceBetween,
  Header,
  Container,
  BreadcrumbGroup,
  Icon,
  Checkbox,
} from "@amzn/awsui-components-react";
import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris";
import { Document } from "models/schemas";
import styles from "./style.module.scss";
import { getFileNameWithExtension } from "components/documents-table-os/utils";
import {
  checkBatchVersionCollisionsOS,
  renderVersionCollisionMessageOS,
} from "components/documents-table-os/version-utils-os";
import { SearchDocumentResponse } from "components/documents-table-os/definitions";
import { GaiaConstants } from "utils/gaia-constants";
import { ServerSideTableWithPropertyFilter } from "components/server-side-table";
import {
  useSearchDocumentsQuery,
  useGetDocumentUniqueValuesQuery,
} from "components/documents-table-os/endpoints";
import {
  MoveFoldersModalOSProps,
  NavigationItem,
  propertyFilterI18nStrings,
  FOLDER_FILTERING_PROPERTIES,
} from "./definitions";
import { SearchDocumentRequest } from "components/documents-table-os/definitions";

const MoveFoldersModalOS: React.FC<MoveFoldersModalOSProps> = ({
  visible,
  setVisible,
  selectedFiles,
  teamName,
  onMove,
  searchDocuments,
}) => {
  const [currentPath, setCurrentPath] = useState<NavigationItem[]>([
    { text: "Team files", parentId: teamName },
  ]);
  const [isMoving, setIsMoving] = useState(false);
  const [collisions, setCollisions] = useState<any[]>([]);
  const [showCollisionWarning, setShowCollisionWarning] = useState(false);
  const [collisionAcknowledged, setCollisionAcknowledged] = useState(false);
  const [isCheckingCollisions, setIsCheckingCollisions] = useState(false);

  const currentParentIdForFolders =
    currentPath[currentPath.length - 1]?.parentId || teamName;

  // Reset collision state when path changes or modal closes
  useEffect(() => {
    setCollisions([]);
    setShowCollisionWarning(false);
    setCollisionAcknowledged(false);
  }, [currentParentIdForFolders, visible]);

  // Handle folder navigation
  const handleFolderClick = (folder: Document) => {
    const folderPath = `${currentParentIdForFolders}/${folder.fileName}`;
    setCurrentPath((prev) => [
      ...prev,
      {
        text: folder.fileName,
        parentId: folderPath,
      },
    ]);
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (targetIndex: number) => {
    setCurrentPath((prev) => prev.slice(0, targetIndex + 1));
  };

  // Handle move operation
  const handleMove = async () => {
    // First click: Check for collisions if not already shown
    if (!showCollisionWarning) {
      setIsCheckingCollisions(true);
      try {
        const detectedCollisions = await checkBatchVersionCollisionsOS(
          selectedFiles,
          () => ({ parentId: currentParentIdForFolders }),
          searchDocuments,
        );
        setIsCheckingCollisions(false);

        if (detectedCollisions.length > 0) {
          setCollisions(detectedCollisions);
          setShowCollisionWarning(true);
          return; // Stop here - show collision warning
        }
        // No collisions, fall through to move
      } catch (error) {
        console.error("Error checking collisions:", error);
        setIsCheckingCollisions(false);
        // Proceed with move even if collision check fails
      }
    }

    // Second click OR no collisions: Proceed with move
    setIsMoving(true);
    try {
      await onMove(selectedFiles, currentParentIdForFolders);
      handleDismissWithReset();
    } catch (error) {
      console.error("Move operation failed:", error);
    } finally {
      setIsMoving(false);
    }
  };

  const handleDismissWithReset = () => {
    setVisible(false);
    // Reset all state
    setCurrentPath([{ text: "Team files", parentId: teamName }]);
    setCollisions([]);
    setShowCollisionWarning(false);
    setCollisionAcknowledged(false);
  };

  const handleDismiss = () => {
    handleDismissWithReset();
  };

  // Breadcrumb items for navigation
  const breadcrumbItems = currentPath.map((item, index) => ({
    text: item.text,
    href: "#",
    onClick: () => {
      handleBreadcrumbClick(index);
    },
  }));

  // Column definitions for folder table
  const folderColumnDefinitions = useMemo(
    () => [
      {
        id: "fileName",
        header: "Folder Name",
        cell: (item: Document) => (
          <Button
            variant="link"
            onClick={() => handleFolderClick(item)}
            ariaLabel={`Navigate to ${item.fileName} folder`}
          >
            <Icon name="folder" />
            <span> {item.fileName}</span>
          </Button>
        ),
        sortingField: "fileName",
        isRowHeader: true,
      },
    ],
    [handleFolderClick],
  );

  // Transform parameters for folder search
  const transformParams = useCallback(
    (params: {
      pagination?: { currentPageIndex?: number; pageSize?: number };
      sorting?: {
        sortingColumn?: { sortingField?: string };
        sortingDescending?: boolean;
      };
      propertyFiltering?: {
        filteringQuery?: PropertyFilterProps.Query;
      };
    }): SearchDocumentRequest => {
      // Build tokens array: always include folder filter + any user search tokens
      const tokens = [
        {
          propertyKey: "extensionType",
          operator: "=",
          value: GaiaConstants.FOLDER_EXTENSION_TYPE,
        },
        ...(params.propertyFiltering?.filteringQuery?.tokens || []),
      ];

      const propertyFilterQuery = {
        tokens,
        operation: "and",
      };

      return {
        teamName,
        parentId: currentParentIdForFolders,
        propertyFilterQuery,
        page: params.pagination?.currentPageIndex || 1,
        pageSize: params.pagination?.pageSize || 25,
        sortBy: params.sorting?.sortingColumn?.sortingField || "fileName",
        sortOrder: params.sorting?.sortingDescending ? "DESC" : "ASC",
      };
    },
    [teamName, currentParentIdForFolders],
  );

  // Transform response
  const transformResponse = useCallback(
    (response: SearchDocumentResponse) => ({
      items: response.documents,
      totalCount: response.paginationMetadata.totalCount,
      filteringOptions: [],
    }),
    [],
  );

  const [folderPreferences, setFolderPreferences] = useState({
    pageSize: 25,
    contentDisplay: [{ id: "fileName", visible: true }],
  });

  // Memoize autocomplete params to update when currentParentIdForFolders changes
  const autocompleteParams = useMemo(
    () => ({
      teamName: teamName || "",
      limit: 150,
      propertyFilterQuery: {
        tokens: [
          {
            propertyKey: "extensionType",
            operator: "=",
            value: GaiaConstants.FOLDER_EXTENSION_TYPE,
          },
          {
            propertyKey: "parentId",
            operator: "=",
            value: currentParentIdForFolders,
          },
        ],
        operation: "and",
      },
    }),
    [teamName, currentParentIdForFolders],
  );

  return (
    <Modal
      onDismiss={handleDismiss}
      visible={visible}
      header="Move Files"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleMove}
              loading={isMoving || isCheckingCollisions}
              disabled={
                isMoving ||
                isCheckingCollisions ||
                (showCollisionWarning && !collisionAcknowledged) ||
                selectedFiles.every(
                  (item) => item.parentId === currentPath.at(-1)?.parentId,
                )
              }
            >
              {isCheckingCollisions
                ? "Checking..."
                : showCollisionWarning
                  ? "Confirm and Move"
                  : "Move to here"}
            </Button>
          </SpaceBetween>
        </Box>
      }
      size="medium"
    >
      <SpaceBetween size="l">
        {/* File selection summary */}
        <Container>
          <Header>Files to move</Header>
          <Box>
            <ul className={styles.fileList}>
              {selectedFiles.map((file) => (
                <li key={file.documentId}>{getFileNameWithExtension(file)}</li>
              ))}
            </ul>
          </Box>
        </Container>

        {/* Version collision warnings */}
        {showCollisionWarning && collisions.length > 0 && (
          <Container
            header={
              <Header variant="h3">
                <span className={styles.warningText}>
                  <Icon name="status-warning" /> Version Collision Warning
                </span>
              </Header>
            }
          >
            <SpaceBetween size="m">
              {renderVersionCollisionMessageOS(collisions)}
              <Checkbox
                checked={collisionAcknowledged}
                onChange={({ detail }) =>
                  setCollisionAcknowledged(detail.checked)
                }
              >
                I understand these files will become versions of existing files
              </Checkbox>
            </SpaceBetween>
          </Container>
        )}

        {/* Navigation */}
        <Container>
          <Header>Select destination folder</Header>
          <SpaceBetween size="s">
            <Box>
              <strong>Current location:</strong>
              <BreadcrumbGroup
                items={breadcrumbItems}
                onClick={(detail) => {
                  detail.detail.item.onClick();
                }}
              />
            </Box>

            {/* Folder list with ServerSideTable */}
            <ServerSideTableWithPropertyFilter
              columnDefinitions={folderColumnDefinitions}
              tableTitle="Available folders"
              dataSource={{
                queryHook: useSearchDocumentsQuery,
                transformParams,
                transformResponse,
                skipQuery: !visible || !teamName,
              }}
              preferencesConfig={{
                saveWidths: () => {},
                preferences: folderPreferences,
                setPreferences: setFolderPreferences,
                contentDisplayPreferenceOptions: [],
                selectionType: undefined,
                defaultPageSize: 25,
                defaultSortingColumnIdx: 0,
                loadingText: "Loading folders...",
                pageSizeOptions: [
                  { value: 10, label: "10 folders" },
                  { value: 25, label: "25 folders" },
                  { value: 50, label: "50 folders" },
                  { value: 100, label: "100 folders" },
                ],
              }}
              propertyFilter={{
                showPropertyFilter: true,
                filteringProperties: FOLDER_FILTERING_PROPERTIES,
                propertyFilterI18nStrings,
                enableFreeTextSearch: true,
                freeTextSearchPlaceholder: "Search folder names...",
                enableTokenSearch: true,
              }}
              autocomplete={{
                uniqueValuesQueryHook: useGetDocumentUniqueValuesQuery,
                uniqueValuesParams: autocompleteParams,
                uniqueValuesConfig: {
                  enabled: true,
                  skipFieldTypes: ["date", "numeric", "freetext"],
                  loadOnlyVisibleColumns: true,
                },
              }}
            />
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Modal>
  );
};

export default MoveFoldersModalOS;
